import { importFileandPreview } from "@rajesh896/video-thumbnails-generator";
import type { LazyBlobPending, LazyBlobRef, LazyBlobValue } from "./types";
import { logVideoCoverBreadcrumb, withVideoCoverSpan } from "./observability";

// Cap canvas dimensions to 1080p to limit memory usage per frame extraction
// (~8 MB per canvas at 1920×1080×4 bytes/pixel).
const MAX_CANVAS_WIDTH = 1920;
const MAX_CANVAS_HEIGHT = 1080;

function clampCanvasDimensions(w: number, h: number) {
  if (w <= MAX_CANVAS_WIDTH && h <= MAX_CANVAS_HEIGHT)
    return { width: w, height: h };
  const scale = Math.min(MAX_CANVAS_WIDTH / w, MAX_CANVAS_HEIGHT / h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

export function isBlobUrl(url: string): boolean {
  return url?.startsWith("blob:");
}

export function revokeBlobUrl(url: string): void {
  if (isBlobUrl(url)) URL.revokeObjectURL(url);
}

export function resolveToastPlatformName(platform: string) {
  switch (platform) {
    case "TT": {
      return "Tiktok";
    }
    case "IG": {
      return "Instagram Reel";
    }
    case "LI":
    case "LINKED": {
      return "LinkedIn";
    }
    default: {
      return "";
    }
  }
}

export function isSafariLikeUserAgent() {
  return (
    navigator.userAgent.includes("Safari") &&
    !navigator.userAgent.includes("Chrome")
  );
}

/**
 * Detect the known play/pause race AbortError that `HTMLMediaElement.play()`
 * raises when a subsequent action interrupts it. We swallow only this
 * specific failure during the Safari workaround; everything else propagates.
 * SKE-3646
 */
export function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error as { name?: string; code?: number };
  if (e.name === "AbortError") {
    return true;
  }
  // Legacy WebKit reports the play() interruption as a DOMException with
  // code 20 (ABORT_ERR).
  return e.code === 20;
}

/**
 * Extract a frame from a video at the specified timestamp.
 * Returns a blob URL (via toBlob + createObjectURL) or falls back to a data URL.
 *
 * Improvements over original implementation:
 * - Safari workarounds (muted, playsInline, play/pause before seek)
 * - Timeout handling to prevent hanging
 * - requestVideoFrameCallback for reliable frame capture
 * - Handles abort/stalled events
 * - Clamps timestamp to avoid edge cases
 */
export function getVideoPreviewImage(
  videoUrl: string,
  timestamp: number,
  timeout = 15_000
): Promise<string> {
  const metadata = {
    videoUrl,
    timestampSeconds: timestamp,
    timeoutMs: timeout,
    navigatorOnline: navigator.onLine,
    windowOrigin: window.location.origin,
    userAgent: navigator.userAgent,
  };

  logVideoCoverBreadcrumb("video_cover.extract_frame.start", metadata);

  return withVideoCoverSpan(
    "video_cover.extract_frame",
    metadata,
    async () =>
      new Promise((resolve, reject) => {
        const videoElement = document.createElement("video");
        videoElement.style.display = "none";
        videoElement.crossOrigin = "Anonymous";
        // Safari workarounds - set BEFORE src
        videoElement.muted = true;
        videoElement.playsInline = true;
        videoElement.setAttribute("webkit-playsinline", "true");
        videoElement.preload = "metadata";
        document.body.append(videoElement);

        const canvasElement = document.createElement("canvas");
        const context = canvasElement.getContext("2d");

        const cleanup = () => {
          clearTimeout(timeoutId);
          videoElement.remove();
        };

        const rejectWithError = (error: unknown, extraMetadata = {}) => {
          const normalizedError =
            error instanceof Error ? error : new Error(String(error));
          const failureMetadata = {
            ...metadata,
            readyState: videoElement.readyState,
            networkState: videoElement.networkState,
            currentSrc: videoElement.currentSrc,
            mediaErrorCode: videoElement?.error?.code,
            ...extraMetadata,
          };

          logVideoCoverBreadcrumb(
            "video_cover.extract_frame.failure",
            failureMetadata
          );
          cleanup();
          reject(normalizedError);
        };

        const timeoutId = setTimeout(() => {
          rejectWithError(
            new Error(`Timeout extracting frame from video: ${videoUrl}`),
            { eventType: "timeout" }
          );
        }, timeout);

        const captureFrame = () => {
          // Ensure video frame is ready
          if (videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
            logVideoCoverBreadcrumb("video_cover.extract_frame.wait_for_data", {
              ...metadata,
              readyState: videoElement.readyState,
            });
            videoElement.addEventListener("loadeddata", captureFrame, {
              once: true,
            });
            return;
          }

          const { width, height } = clampCanvasDimensions(
            videoElement.videoWidth,
            videoElement.videoHeight
          );
          canvasElement.width = width;
          canvasElement.height = height;
          context?.drawImage(videoElement, 0, 0, width, height);

          try {
            if (typeof canvasElement.toBlob === "function") {
              canvasElement.toBlob(
                (blob) => {
                  if (blob) {
                    const previewUrl = URL.createObjectURL(blob);
                    logVideoCoverBreadcrumb(
                      "video_cover.extract_frame.success",
                      {
                        ...metadata,
                        outputKind: "blob",
                        width,
                        height,
                      }
                    );
                    cleanup();
                    resolve(previewUrl);
                  } else {
                    try {
                      const dataUrl = canvasElement.toDataURL(
                        "image/jpeg",
                        0.85
                      );
                      logVideoCoverBreadcrumb(
                        "video_cover.extract_frame.success",
                        {
                          ...metadata,
                          outputKind: "data_url",
                          width,
                          height,
                        }
                      );
                      cleanup();
                      resolve(dataUrl);
                    } catch {
                      rejectWithError(
                        new Error(`Canvas failed for video: ${videoUrl}`),
                        { eventType: "canvas" }
                      );
                    }
                  }
                },
                "image/jpeg",
                0.85
              );
            } else {
              const dataUrl = canvasElement.toDataURL("image/jpeg", 0.85);
              logVideoCoverBreadcrumb("video_cover.extract_frame.success", {
                ...metadata,
                outputKind: "data_url",
                width,
                height,
              });
              cleanup();
              resolve(dataUrl);
            }
          } catch {
            rejectWithError(
              new Error(`Canvas tainted or failed for video: ${videoUrl}`),
              { eventType: "canvas_tainted" }
            );
          }
        };

        videoElement.addEventListener("loadedmetadata", async () => {
          logVideoCoverBreadcrumb("video_cover.extract_frame.loadedmetadata", {
            ...metadata,
            duration: videoElement.duration,
          });

          if (isSafariLikeUserAgent()) {
            try {
              await videoElement.play();
              videoElement.pause();
            } catch (error) {
              if (!isAbortError(error)) {
                rejectWithError(error, { eventType: "play_pause" });
                return;
              }
            }
          }

          const duration = videoElement.duration;
          const clampedTime =
            !Number.isFinite(duration) || duration <= 0
              ? 0
              : Math.max(0, Math.min(timestamp, duration - 0.001));

          logVideoCoverBreadcrumb("video_cover.extract_frame.seek", {
            ...metadata,
            clampedTime,
            duration,
          });
          videoElement.currentTime = clampedTime;
        });

        videoElement.addEventListener("seeked", () => {
          logVideoCoverBreadcrumb("video_cover.extract_frame.seeked", metadata);
          captureFrame();
        });

        const handleError = (event: Event) => {
          rejectWithError(
            new Error(
              `Error loading video. Video URL: ${videoUrl}. Event: ${event?.type}. Status: ${videoElement?.error?.code}.`
            ),
            { eventType: event?.type }
          );
        };

        videoElement.addEventListener("error", handleError);
        videoElement.addEventListener("abort", handleError);
        videoElement.addEventListener("stalled", () => {
          console.warn(`Video stalled while loading: ${videoUrl}`);
          logVideoCoverBreadcrumb("video_cover.extract_frame.stalled", {
            ...metadata,
            readyState: videoElement.readyState,
            networkState: videoElement.networkState,
          });
        });

        videoElement.src = videoUrl;
        videoElement.load();
      })
  );
}

// Errors that should NOT be retried (non-transient)
const NON_RETRYABLE_ERRORS = [
  "Status: 4", // MEDIA_ERR_SRC_NOT_SUPPORTED
  "Canvas tainted",
  "SecurityError",
];

function isRetryableError(error: Error): boolean {
  return !NON_RETRYABLE_ERRORS.some((msg) => error.message.includes(msg));
}

/**
 * Wrapper around getVideoPreviewImage with retry logic for transient errors.
 * Does NOT retry for non-transient errors (unsupported format, CORS, etc.)
 */
export async function getVideoPreviewImageWithRetry(
  videoUrl: string,
  timestamp: number,
  maxRetries = 2,
  timeout = 15_000
): Promise<string> {
  let lastError: Error | null = null;
  const metadata = {
    videoUrl,
    timestampSeconds: timestamp,
    maxRetries,
    timeoutMs: timeout,
  };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      logVideoCoverBreadcrumb("video_cover.extract_frame.attempt", {
        ...metadata,
        attempt,
      });
      return await getVideoPreviewImage(videoUrl, timestamp, timeout);
    } catch (error) {
      lastError = error as Error;
      logVideoCoverBreadcrumb("video_cover.extract_frame.attempt_failed", {
        ...metadata,
        attempt,
        errorMessage: lastError.message,
      });

      // Don't retry non-transient errors
      if (!isRetryableError(lastError)) {
        throw lastError;
      }

      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }

  throw lastError;
}

/**
 * Module-level single-flight cache for the legacy "download the full video
 * and extract a frame locally" fallback. Cluster posts mount three or four
 * sibling forms that each call this function with the same original `.mp4`
 * URL within the same tick; without this cache we ended up issuing several
 * concurrent ~200 MB downloads for the same asset. SKE-3646
 *
 * The cache only tracks IN-FLIGHT promises. On both resolve and reject the
 * entry is removed so the Blob itself is never retained globally — that's
 * the per-form `LazyBlobRef` cleanup's job.
 */
const inFlightVideoBlobRequests = new Map<string, Promise<Blob>>();

export const convertVideoUrlToBlob = async (url: string): Promise<Blob> => {
  const existing = inFlightVideoBlobRequests.get(url);
  if (existing) {
    logVideoCoverBreadcrumb("video_cover.fetch_blob.reused", { url });
    return existing;
  }
  logVideoCoverBreadcrumb("video_cover.fetch_blob.start", {
    url,
    navigatorOnline: navigator.onLine,
    windowOrigin: window.location.origin,
  });
  const request = withVideoCoverSpan(
    "video_cover.fetch_blob",
    {
      url,
      navigatorOnline: navigator.onLine,
      windowOrigin: window.location.origin,
      userAgent: navigator.userAgent,
    },
    async () => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch video. Status: ${response.status} ${response.statusText}. URL: ${url}.`
        );
      }
      logVideoCoverBreadcrumb("video_cover.fetch_blob.success", {
        url,
        status: response.status,
      });
      return response.blob();
    }
  ).catch((error) => {
    logVideoCoverBreadcrumb("video_cover.fetch_blob.failure", {
      url,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    throw error;
  });
  inFlightVideoBlobRequests.set(url, request);
  try {
    return await request;
  } finally {
    inFlightVideoBlobRequests.delete(url);
  }
};

// Exported for tests only.
export const __getInFlightVideoBlobRequests = () => inFlightVideoBlobRequests;

export function ensureFile(
  blob: Blob,
  fileName = "video-cover-source.mp4"
): File {
  if (blob instanceof File) {
    return blob;
  }

  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

function hasResolvedLazyBlob(
  value: LazyBlobRef["current"]
): value is LazyBlobValue {
  return Boolean(value && "blobUrl" in value);
}

function hasPendingLazyBlob(
  value: LazyBlobRef["current"]
): value is LazyBlobPending {
  return Boolean(value && "promise" in value);
}

export function clearLazyBlobRef(blobRef: LazyBlobRef): void {
  if (hasResolvedLazyBlob(blobRef.current)) {
    revokeBlobUrl(blobRef.current.blobUrl);
  } else if (hasPendingLazyBlob(blobRef.current)) {
    blobRef.current.cancelled = true;
  }

  blobRef.current = null;
}

/**
 * Lazily creates a video blob from a remote URL on demand.
 * Caches the result in a ref so subsequent calls reuse the same blob.
 * Used as a CORS-safe fallback when remote frame extraction fails.
 */
export async function getOrCreateVideoBlob(
  mediaUrl: string,
  blobRef: LazyBlobRef
): Promise<LazyBlobValue> {
  if (hasResolvedLazyBlob(blobRef.current)) {
    return blobRef.current;
  }

  if (hasPendingLazyBlob(blobRef.current)) {
    return blobRef.current.promise;
  }

  const pendingValue: LazyBlobPending = {
    cancelled: false,
    promise: Promise.resolve(null as never),
  };
  const promise = (async () => {
    const file = ensureFile(await convertVideoUrlToBlob(mediaUrl));
    const blobUrl = await importFileandPreview(file);
    const resolvedValue = { file, blobUrl };

    if (pendingValue.cancelled || blobRef.current !== pendingValue) {
      revokeBlobUrl(blobUrl);
      throw new Error("Lazy video blob request was cleared");
    }

    blobRef.current = resolvedValue;
    return resolvedValue;
  })().catch((error) => {
    if (blobRef.current === pendingValue) {
      blobRef.current = null;
    }
    throw error;
  });

  pendingValue.promise = promise;
  blobRef.current = pendingValue;
  return promise;
}
